import { useId, useState } from 'react';

import { css } from '@compiled/react';

import PremiumIcon from '@atlaskit/icon/core/premium';
import { token } from '@atlaskit/tokens';

import { Image } from '@/libraries/design/components/image/src/image';
import { wacToken } from '@/libraries/design/foundations';

import {
	PersonalValueProp,
	EducationValueProp,
	RoleOptions,
	UseCaseIcons,
	UseCaseLabels,
} from '../utilities/types';

import { LoomOnboardingHeading } from './loom-onboarding-heading';
import { LoomOnboardingRoleSelect } from './loom-onboarding-role-select';
import { LoomOnboardingText } from './loom-onboarding-text';

import type { Role, UseCase } from '../utilities/types';

const cardStyles = css({
	display: 'flex',
	flexDirection: 'column',
	width: '100%',
	borderStyle: 'solid',
	borderWidth: '1px',
	borderColor: token('color.border'),
	borderRadius: '10px',
	// Hardcoded white so the card reads as a solid surface regardless of
	// whether an ADS ThemeProvider is present in this view.
	backgroundColor: wacToken('color.neutral.light0'),
	transition:
		'border-color 100ms ease-out, border-width 100ms ease-out, background-color 100ms ease-out',
	// Hover feedback lives on the card (not the inner button) so the
	// selected card's blue fill is never painted over by the button hover.
	':hover': {
		backgroundColor: token('color.background.neutral.subtle.hovered'),
	},
});

const cardSelectedStyles = css({
	borderWidth: '2px',
	borderColor: token('color.border.selected'),
	// Light-blue "selected" fill from the design mock. Repeated in the
	// `:hover` block so the selected fill isn't overwritten by the
	// card's default neutral hover background.
	backgroundColor: wacToken('color.extended.blue100'),
	':hover': {
		backgroundColor: wacToken('color.extended.blue100'),
	},
});

// Standalone panel that appears below the "Work" card when it's selected.
// Rendered as its own card (not connected to the trigger above) with the
// #F8F8F8 background from the mock.
const detailCardStyles = css({
	display: 'flex',
	flexDirection: 'column',
	gap: '10px',
	width: '100%',
	borderRadius: '10px',
	// Closest design-token neutral surface to the requested #F8F8F8. Fall
	// back to the literal hex so the color matches the mock even in themes
	// that map the token differently.
	backgroundColor: wacToken('color.neutral.light100'),
	paddingBlock: '18px',
	paddingInline: '20px',
	marginBlockStart: '12px',
	textAlign: 'left',
});

const triggerStyles = css({
	display: 'flex',
	alignItems: 'center',
	gap: token('space.100'),
	width: '100%',
	height: '60px',
	paddingBlock: 0,
	paddingInline: token('space.100'),
	backgroundColor: 'transparent',
	borderWidth: 0,
	borderStyle: 'solid',
	// Match the card's inner rounding (card is 10px radius with a 1px
	// border → 9px inner radius) so the hover background doesn't render as
	// a square inside the rounded card.
	borderRadius: '9px',
	margin: 0,
	textAlign: 'left',
	cursor: 'pointer',
	// Focus ring is rendered on the parent card via `:focus-within` so it
	// wraps the card with concentric rounded corners. Suppress the button's
	// own outline to avoid a doubled focus indicator. The hover feedback
	// also lives on the parent card so it doesn't paint over the selected
	// card's blue fill.
	':focus': {
		outline: 'none',
	},
	':focus-visible': {
		outline: 'none',
	},
});

const iconTileStyles = css({
	display: 'inline-flex',
	alignItems: 'center',
	justifyContent: 'center',
	width: '40px',
	height: '40px',
	borderRadius: '6px',
	flexShrink: 0,
});

const sparkleStyles = css({
	display: 'inline-flex',
	alignItems: 'center',
	justifyContent: 'center',
	flexShrink: 0,
	// Matches the adjacent blurb text hex; PremiumIcon inherits via currentColor.
	color: wacToken('color.neutral.light700'),
});

// Value-prop blurb that appears under a non-work card when it's not
// selected. Adds ~12px of breathing room above the text so it sits
// away from the card border, matching the mock spacing.
const valuePropWrapperStyles = css({
	paddingBlockStart: '12px',
});

const blurbStyles = css({
	display: 'flex',
	alignItems: 'center',
	gap: '6px',
	textAlign: 'left',
});

export type UseCaseButtonProps = {
	/** The use case this card represents. */
	useCase: UseCase;
	/** Whether this card is currently selected. */
	isSelected: boolean;
	/** Called when the user picks this use case card. */
	onSelect: (useCase: UseCase) => void;
	/** Currently selected role (only meaningful when useCase === 'work'). */
	role: Role | undefined;
	/** Called when the user changes the role in the Work detail select. */
	onRoleChange: (role: Role) => void;
};

export const UseCaseButton = ({
	useCase,
	isSelected,
	onSelect,
	role,
	onRoleChange,
}: UseCaseButtonProps) => {
	const roleSelectId = useId();
	const roleLabelId = `${roleSelectId}-label`;
	const [isFocused, setIsFocused] = useState(false);

	const label = UseCaseLabels[useCase];
	const icon = UseCaseIcons[useCase];
	const showWorkDetail = useCase === 'work' && isSelected;

	const currentBlurb = RoleOptions.find((option) => option.value === role)?.valueProp;

	return (
		<li>
			<div
				css={[cardStyles, isSelected && cardSelectedStyles]}
				style={{
					// Applied via inline style so it beats Compiled's atomic
					// class ordering — the selected fill was being dropped
					// when merged with the base white background.
					...(isSelected ? { backgroundColor: wacToken('color.extended.blue100') } : null),
					...(isFocused
						? {
								outline: `2px solid ${token('color.border.focused')}`,
								outlineOffset: '3px',
							}
						: null),
				}}
			>
				<button
					type="button"
					onClick={() => onSelect(useCase)}
					onFocus={(event) => {
						setIsFocused(event.currentTarget.matches(':focus-visible'));
					}}
					onBlur={() => setIsFocused(false)}
					aria-pressed={isSelected}
					aria-expanded={useCase === 'work' ? isSelected : undefined}
					aria-controls={useCase === 'work' ? 'loom-onboarding-work-detail' : undefined}
					css={triggerStyles}
					data-testid={`loom-onboarding-use-case-${useCase}`}
				>
					<span aria-hidden="true" css={iconTileStyles}>
						<Image src={icon} alt="" />
					</span>
					<LoomOnboardingHeading level={3} size="sm">
						{label}
					</LoomOnboardingHeading>
				</button>
			</div>

			{showWorkDetail && (
				<div
					id="loom-onboarding-work-detail"
					css={detailCardStyles}
					data-testid="loom-onboarding-work-detail"
				>
					<label id={roleLabelId} htmlFor={roleSelectId}>
						<LoomOnboardingText size="sm" inline color={token('color.text')}>
							What type of work do you do?
						</LoomOnboardingText>
					</label>
					<LoomOnboardingRoleSelect
						id={roleSelectId}
						labelledBy={roleLabelId}
						value={role}
						onChange={onRoleChange}
					/>
					{currentBlurb && (
						<div css={blurbStyles}>
							<span aria-hidden="true" css={sparkleStyles}>
								<PremiumIcon label="" color="currentColor" />
							</span>
							<LoomOnboardingText size="sm" inline color="#6B6E76">
								{currentBlurb}
							</LoomOnboardingText>
						</div>
					)}
				</div>
			)}
			{isSelected && useCase !== 'work' && (
				<div css={valuePropWrapperStyles}>
					<LoomOnboardingText color="#6B6E76">
						{useCase === 'education' ? EducationValueProp : PersonalValueProp}
					</LoomOnboardingText>
				</div>
			)}
		</li>
	);
};
